// =============================================================================
// SimSense Platform — Public Coach Profile Page (B2B-003)
// =============================================================================

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../utils/api";

const SERVICE_TYPE_LABELS: Record<string, string> = {
  async_review: "Async Telemetry Review",
  live_session: "Live Coaching Session",
  package: "Multi-Session Package",
  office_hours: "Office Hours",
  bootcamp: "Bootcamp / Cohort",
};

export function CoachProfile() {
  const { slug } = useParams<{ slug: string }>();
  const [coach, setCoach] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await api<any>(`/api/coach-platform/profiles/${slug}`);
        setCoach(data.profile || data);
        setServices(data.services || []);
        document.title = `${data.profile?.brandName || data.brand_name || "Coach"} | SimSense`;
      } catch {
        // Profile not found
      }
      setLoading(false);
    }
    if (slug) load();
  }, [slug]);

  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Loading coach profile...</div>;

  if (!coach) {
    return (
      <div style={{ padding: "60px", textAlign: "center" }}>
        <h2 style={{ marginBottom: "8px" }}>Coach Not Found</h2>
        <p style={{ color: "var(--text2)" }}>This coaching profile doesn't exist or isn't public yet.</p>
        <Link to="/coaches" style={{ color: "var(--accent)", marginTop: "12px", display: "inline-block" }}>Browse all coaches</Link>
      </div>
    );
  }

  const brandName = coach.brandName || coach.brand_name || "Coach";
  const headline = coach.headline || "";
  const bio = coach.bio || "";
  const specialties = coach.specialties || [];
  const disciplines = coach.supportedDisciplines || coach.supported_disciplines || [];
  const asyncPrice = coach.asyncReviewPriceCents || coach.async_review_price_cents || 0;
  const hourlyRate = coach.hourlyRateCents || coach.hourly_rate_cents || 0;
  const responseSla = coach.responseSlaHours || coach.response_sla_hours || 0;

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      {/* Breadcrumb */}
      <div style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "16px" }}>
        <Link to="/coaches" style={{ color: "var(--accent)", textDecoration: "none" }}>Coaches</Link>
        {" / "}{brandName}
      </div>

      {/* Profile Header */}
      <section style={{ background: "var(--card)", padding: "32px", borderRadius: "var(--radius)", border: "1px solid var(--border)", marginBottom: "20px" }}>
        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
          <div style={{
            width: "80px", height: "80px", borderRadius: "50%", background: "var(--accent)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "32px", fontWeight: 700, color: "#fff",
            flexShrink: 0,
          }}>
            {brandName.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: "24px", marginBottom: "4px" }}>{brandName}</h1>
            {headline && <p style={{ color: "var(--text2)", fontSize: "14px", marginBottom: "12px" }}>{headline}</p>}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
              {disciplines.map((d: string) => (
                <span key={d} style={{ padding: "4px 12px", borderRadius: "16px", fontSize: "11px", fontWeight: 600, background: "rgba(59,130,246,0.12)", color: "var(--accent)" }}>{d}</span>
              ))}
            </div>
            {/* Quick stats */}
            <div style={{ display: "flex", gap: "24px", fontSize: "13px" }}>
              {asyncPrice > 0 && <div><span style={{ color: "var(--text3)" }}>Review: </span><strong>${(asyncPrice / 100).toFixed(0)}</strong></div>}
              {hourlyRate > 0 && <div><span style={{ color: "var(--text3)" }}>Hourly: </span><strong>${(hourlyRate / 100).toFixed(0)}/hr</strong></div>}
              {responseSla > 0 && <div><span style={{ color: "var(--text3)" }}>Response: </span><strong>{responseSla}h</strong></div>}
            </div>
          </div>
        </div>
      </section>

      {/* Bio */}
      {bio && (
        <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)", marginBottom: "20px" }}>
          <h3 style={{ fontSize: "14px", textTransform: "uppercase", color: "var(--text2)", marginBottom: "12px" }}>About</h3>
          <p style={{ fontSize: "14px", lineHeight: 1.6, color: "var(--text1)", whiteSpace: "pre-wrap" }}>{bio}</p>
        </section>
      )}

      {/* Specialties */}
      {specialties.length > 0 && (
        <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)", marginBottom: "20px" }}>
          <h3 style={{ fontSize: "14px", textTransform: "uppercase", color: "var(--text2)", marginBottom: "12px" }}>Specialties</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {specialties.map((s: string) => (
              <span key={s} style={{ padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 600, background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", color: "var(--text1)" }}>{s}</span>
            ))}
          </div>
        </section>
      )}

      {/* Services */}
      <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
        <h3 style={{ fontSize: "14px", textTransform: "uppercase", color: "var(--text2)", marginBottom: "16px" }}>Services</h3>
        {services.length === 0 ? (
          <p style={{ color: "var(--text3)", fontSize: "13px" }}>No services published yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {services.map((svc: any) => (
              <div key={svc.id} style={{ padding: "16px", background: "rgba(255,255,255,0.03)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "2px" }}>{svc.title}</div>
                    <div style={{ fontSize: "11px", color: "var(--accent)" }}>{SERVICE_TYPE_LABELS[svc.serviceType || svc.service_type] || svc.service_type}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, fontSize: "18px", color: "#fff" }}>${((svc.priceCents || svc.price_cents || 0) / 100).toFixed(0)}</div>
                    {(svc.durationMinutes || svc.duration_minutes) && <div style={{ fontSize: "11px", color: "var(--text3)" }}>{svc.durationMinutes || svc.duration_minutes} min</div>}
                  </div>
                </div>
                {svc.description && <p style={{ fontSize: "13px", color: "var(--text2)", lineHeight: 1.5 }}>{svc.description}</p>}
                {(svc.deliverySlaHours || svc.delivery_sla_hours) && (
                  <div style={{ fontSize: "11px", color: "var(--text3)", marginTop: "8px" }}>Delivered within {svc.deliverySlaHours || svc.delivery_sla_hours} hours</div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
